<template>
  <div class="list-page-container">
    <!-- 统一控制中心 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-pie-chart"></i>
          <span>{{ $t('resourceManagement.resourceReportTips') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button v-if="hasPermission('sys:resource:report')" type="primary" @click="handleGenerateReport" icon="el-icon-document">
            {{ $t('resourceManagement.generateReport') }}
          </el-button>
          <el-button v-if="hasPermission('sys:resource:export')" type="success" plain @click="handleExportReport" :loading="exportLoading" icon="el-icon-download">
            {{ $t('common.export') }}
          </el-button>
          <el-button @click="handleRefresh" icon="el-icon-refresh">
            {{ $t('common.refresh') }}
          </el-button>
        </div>
      </div>
    </div>
    
    <div class="report-main-content">
      <!-- 报表配置 -->
      <el-card class="report-config-card custom-card" shadow="never">
        <div slot="header" class="card-header">
          <div class="header-title">
            <i class="el-icon-setting"></i>
            <span>{{ $t('resourceManagement.reportConfiguration') }}</span>
          </div>
        </div>
        <el-form :model="reportForm" label-width="120px" class="config-form">
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-form-item :label="$t('resourceManagement.reportType')">
                <el-select v-model="reportForm.reportType" :placeholder="$t('common.selectPlaceholder')" style="width: 100%;">
                  <el-option :label="$t('resourceManagement.usageSummary')" value="usage_summary"></el-option>
                  <el-option :label="$t('resourceManagement.trendAnalysis')" value="trend_analysis"></el-option>
                  <el-option :label="$t('resourceManagement.costAnalysis')" value="cost_analysis"></el-option>
                  <el-option :label="$t('resourceManagement.forecastReport')" value="forecast_report"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="12" :lg="10">
              <el-form-item :label="$t('resourceManagement.timeRange')">
                <el-date-picker
                  v-model="reportForm.timeRange"
                  type="datetimerange"
                  :start-placeholder="$t('common.startDate')"
                  :end-placeholder="$t('common.endDate')"
                  format="yyyy-MM-dd HH:mm:ss"
                  value-format="yyyy-MM-dd HH:mm:ss"
                  style="width: 100%;"
                  class="filter-date-picker"
                ></el-date-picker>
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-form-item :label="$t('resourceManagement.resourceTypes')">
            <el-checkbox-group v-model="reportForm.resourceTypes">
              <el-checkbox label="compute">{{ $t('resourceManagement.computeResources') }}</el-checkbox>
              <el-checkbox label="storage">{{ $t('resourceManagement.storageResources') }}</el-checkbox>
              <el-checkbox label="network">{{ $t('resourceManagement.networkResources') }}</el-checkbox>
              <el-checkbox label="memory">{{ $t('resourceManagement.memoryResources') }}</el-checkbox>
            </el-checkbox-group>
          </el-form-item>
          
          <el-form-item :label="$t('common.description')">
            <el-input 
              type="textarea" 
              v-model="reportForm.description" 
              :placeholder="$t('common.descriptionPlaceholder')"
              :rows="2"
              style="max-width: 800px;"
            ></el-input>
          </el-form-item>
          
          <el-form-item class="form-actions">
            <el-button v-if="hasPermission('sys:resource:report')" type="primary" @click="handlePreviewReport" icon="el-icon-view">{{ $t('resourceManagement.previewReport') }}</el-button>
            <el-button v-if="hasPermission('sys:resource:report')" type="success" @click="handleGenerateReport" icon="el-icon-check">{{ $t('resourceManagement.generateReport') }}</el-button>
          </el-form-item>
        </el-form>
      </el-card>
      
      <!-- 报表预览 -->
      <el-card class="report-preview-card custom-card" v-if="reportVisible" shadow="never">
        <div slot="header" class="card-header">
          <div class="header-title">
            <i class="el-icon-monitor"></i>
            <span>{{ $t('resourceManagement.reportPreview') }}</span>
          </div>
          <div class="header-actions">
            <el-button v-if="hasPermission('sys:resource:export')" type="success" plain @click="handleExportReport" :loading="exportLoading" icon="el-icon-download">
              {{ $t('common.export') }}
            </el-button>
          </div>
        </div>
        
        <!-- 加载状态 -->
        <div v-if="reportLoading" class="report-loading">
          <el-skeleton :rows="10" animated />
        </div>
        
        <!-- 报表内容 -->
        <div v-else class="report-content">
          <div class="report-header">
            <h2 class="report-title">{{ $t('resourceManagement.resourceUsageReport') }}</h2>
            <div class="report-meta">
              <div class="meta-item">
                <span class="label">{{ $t('resourceManagement.reportType') }}:</span>
                <el-tag size="mini" effect="plain">{{ getReportTypeText(reportForm.reportType) }}</el-tag>
              </div>
              <div class="meta-item">
                <span class="label">{{ $t('resourceManagement.timeRange') }}:</span>
                <span class="value">{{ reportForm.timeRange[0] }} ~ {{ reportForm.timeRange[1] }}</span>
              </div>
              <div class="meta-item">
                <span class="label">{{ $t('resourceManagement.generateTime') }}:</span>
                <span class="value">{{ formatDateTime(new Date()) }}</span>
              </div>
            </div>
          </div>
          
          <div class="report-section">
            <div class="section-title">
              <i class="el-icon-pie-chart"></i>
              <span>{{ $t('resourceManagement.overview') }}</span>
            </div>
            <div class="overview-grid">
              <div v-for="(val, key) in overviewCards" :key="key" class="overview-stat-card">
                <div class="stat-icon" :class="key">
                  <i :class="val.icon"></i>
                </div>
                <div class="stat-info">
                  <div class="stat-label">{{ val.label }}</div>
                  <div class="stat-value-wrapper">
                    <span class="stat-value">{{ val.value }}</span>
                    <span v-if="val.unit" class="stat-unit">{{ val.unit }}</span>
                  </div>
                  <div class="stat-change" :class="val.change >= 0 ? 'up' : 'down'">
                    <i :class="val.change >= 0 ? 'el-icon-caret-top' : 'el-icon-caret-bottom'"></i>
                    <span>{{ Math.abs(val.change) }}%</span>
                    <span class="change-label">{{ $t('resourceManagement.vsLastPeriod') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <el-row :gutter="20">
            <el-col :xs="24" :lg="16">
              <div class="report-section" v-if="reportForm.reportType !== 'forecast_report'">
                <div class="section-title">
                  <i class="el-icon-set-up"></i>
                  <span>{{ $t('resourceManagement.resourceUsageTrend') }}</span>
                </div>
                <div class="chart-container card-style">
                  <v-chart
                    :option="trendChartOption"
                    :autoresize="true"
                    height="350px"
                  ></v-chart>
                </div>
              </div>
            </el-col>
            <el-col :xs="24" :lg="8">
              <div class="report-section">
                <div class="section-title">
                  <i class="el-icon-pie-chart"></i>
                  <span>{{ $t('resourceManagement.resourceDistribution') }}</span>
                </div>
                <div class="chart-container card-style">
                  <v-chart
                    :option="distributionChartOption"
                    :autoresize="true"
                    height="350px"
                  ></v-chart>
                </div>
              </div>
            </el-col>
          </el-row>
          
          <div class="report-section">
            <div class="section-title">
              <i class="el-icon-document-copy"></i>
              <span>{{ $t('resourceManagement.distributionDetails') }}</span>
            </div>
            <el-table :data="reportData.distributionData" style="width: 100%" class="custom-table">
              <el-table-column prop="type" :label="$t('resourceManagement.resourceType')" align="center"></el-table-column>
              <el-table-column prop="usage" :label="$t('resourceManagement.usage')" align="center">
                <template slot-scope="scope">
                  <span class="font-medium">{{ scope.row.usage }}</span>
                  <span class="text-secondary ml-1">{{ scope.row.unit }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="percentage" :label="$t('resourceManagement.percentage')" min-width="150" align="center">
                <template slot-scope="scope">
                  <el-progress :percentage="scope.row.percentage" :color="getChartColor(scope.$index)" :stroke-width="10"></el-progress>
                </template>
              </el-table-column>
              <el-table-column prop="cost" :label="$t('resourceManagement.cost')" align="center">
                <template slot-scope="scope">
                  <span class="text-primary font-bold">¥{{ scope.row.cost }}</span>
                </template>
              </el-table-column>
            </el-table>
          </div>
          
          <div class="report-section">
            <div class="section-title">
              <i class="el-icon-warning-outline"></i>
              <span>{{ $t('resourceManagement.alertsAndEvents') }}</span>
            </div>
            <el-table :data="reportData.alerts" style="width: 100%" class="custom-table">
              <el-table-column prop="time" :label="$t('common.time')" width="180" align="center"></el-table-column>
              <el-table-column prop="type" :label="$t('common.type')" width="120" align="center">
                <template slot-scope="scope">
                  <el-tag :type="getAlertType(scope.row.type)" size="mini" effect="plain">
                    {{ $t(`resourceManagement.${scope.row.type}`) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="description" :label="$t('common.description')" min-width="200"></el-table-column>
              <el-table-column prop="resource" :label="$t('resourceManagement.resourceType')" width="150" align="center"></el-table-column>
              <el-table-column prop="impact" :label="$t('resourceManagement.impact')" width="120" align="center">
                <template slot-scope="scope">
                  <span :class="getImpactClass(scope.row.impact)" class="font-medium">
                    {{ $t(`resourceManagement.${scope.row.impact}Impact`) }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
          </div>
          
          <div class="report-section">
            <div class="section-title">
              <i class="el-icon-magic-stick"></i>
              <span>{{ $t('resourceManagement.recommendations') }}</span>
            </div>
            <div class="recommendations-list">
              <div v-for="(rec, index) in reportData.recommendations" :key="index" class="recommendation-card" :class="rec.type">
                <div class="rec-icon">
                  <i :class="getRecIcon(rec.type)"></i>
                </div>
                <div class="rec-body">
                  <div class="rec-title">{{ rec.title }}</div>
                  <div class="rec-desc">{{ rec.description }}</div>
                </div>
                <div class="rec-action">
                  <el-button type="text" size="small" icon="el-icon-right">{{ $t('common.viewDetails') }}</el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import ECharts from '@/components/charts/ECharts.vue'
import { hasPermission } from '@/utils/permission'

const CHART_COLORS = ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#722ED1', '#13C2C2', '#EB2F96']

export default {
  name: 'ResourceReport',
  components: {
    'v-chart': ECharts
  },
  data() {
    return {
      reportLoading: false,
      exportLoading: false,
      reportVisible: false,
      reportForm: {
        reportType: 'usage_summary',
        timeRange: [this.getDateBeforeDays(7), this.getDateNow()],
        resourceTypes: ['compute', 'storage', 'network'],
        description: ''
      },
      reportData: {
        overview: {
          totalUsage: '1,245',
          totalUsageChange: 5.2,
          averageUsage: '87',
          averageUsageChange: -2.1,
          peakUsage: '1,450',
          peakUsageChange: 3.7,
          totalCost: '3,250',
          totalCostChange: 8.3
        },
        distributionData: [
          { type: '计算资源', usage: '650', unit: '小时', percentage: 45, cost: '1,200' },
          { type: '存储资源', usage: '350', unit: 'GB', percentage: 25, cost: '800' },
          { type: '网络资源', usage: '145', unit: 'GB', percentage: 20, cost: '950' },
          { type: '内存资源', usage: '100', unit: 'GB', percentage: 10, cost: '300' }
        ],
        alerts: [
          { time: '2023-05-20 14:23:15', type: 'warning', description: '存储资源使用量达到警告阈值', resource: '存储资源', impact: 'medium' },
          { time: '2023-05-18 09:45:30', type: 'error', description: '网络资源使用量超过限制', resource: '网络资源', impact: 'high' },
          { time: '2023-05-15 16:12:05', type: 'info', description: '计算资源使用量恢复正常', resource: '计算资源', impact: 'low' }
        ],
        recommendations: [
          { type: 'optimization', title: '优化存储配置', description: '当前存储资源使用率偏低，建议调整配额分配以提高资源利用率。' },
          { type: 'warning', title: '关注网络资源', description: '网络资源使用量波动较大，建议设置更严格的监控阈值。' },
          { type: 'info', title: '资源使用趋势', description: '未来一周预计资源使用量将增长15%，建议提前做好容量规划。' }
        ]
      }
    }
  },
  computed: {
    overviewCards() {
      const data = this.reportData.overview
      return {
        usage: { label: this.$t('resourceManagement.totalUsage'), value: data.totalUsage, change: data.totalUsageChange, icon: 'el-icon-data-line' },
        average: { label: this.$t('resourceManagement.averageUsage'), value: data.averageUsage, change: data.averageUsageChange, icon: 'el-icon-odometer' },
        peak: { label: this.$t('resourceManagement.peakUsage'), value: data.peakUsage, change: data.peakUsageChange, icon: 'el-icon-top' },
        cost: { label: this.$t('resourceManagement.totalCost'), value: '¥' + data.totalCost, change: data.totalCostChange, icon: 'el-icon-money' }
      }
    },
    trendChartOption() {
      return {
        color: CHART_COLORS,
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          borderColor: '#f0f2f5',
          borderWidth: 1,
          padding: [12, 16],
          textStyle: { color: '#303133', fontSize: 13 },
          extraCssText: 'box-shadow: 0 4px 16px rgba(0,0,0,0.08); border-radius: 8px;',
          axisPointer: {
            type: 'line',
            lineStyle: {
              color: 'rgba(64, 158, 255, 0.2)',
              width: 1
            }
          },
          formatter: (params) => {
            let res = `<div style="font-weight: 600; margin-bottom: 8px; color: #303133; font-size: 14px;">${params[0].name}</div>`;
            params.forEach(item => {
              res += `<div style="display: flex; align-items: center; justify-content: space-between; gap: 24px;">
                <span style="display: flex; align-items: center; color: #606266;">
                  <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${item.color}; margin-right: 8px;"></span>
                  ${item.seriesName}
                </span>
                <span style="font-weight: 600; color: #303133">${item.value.toLocaleString()}</span>
              </div>`;
            });
            return res;
          }
        },
        legend: {
          icon: 'circle',
          itemWidth: 8,
          itemHeight: 8,
          data: [
            this.$t('resourceManagement.computeResources'),
            this.$t('resourceManagement.storageResources'),
            this.$t('resourceManagement.networkResources'),
            this.$t('resourceManagement.memoryResources')
          ],
          bottom: 0,
          textStyle: { fontSize: 12, color: '#909399' }
        },
        grid: {
          left: '20',
          right: '25',
          bottom: '40',
          top: '20',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.generateTimeLabels(),
          axisLine: { lineStyle: { color: '#f0f2f5' } },
          axisLabel: { color: '#94a3b8', fontSize: 11, margin: 12 },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { type: 'solid', color: '#f5f7fa' } },
          axisLabel: { color: '#94a3b8', fontSize: 11, margin: 12 }
        },
        series: [
          {
            name: this.$t('resourceManagement.computeResources'),
            type: 'line',
            smooth: 0.4,
            showSymbol: false,
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(64, 158, 255, 0.2)' },
                { offset: 1, color: 'rgba(64, 158, 255, 0)' }
              ])
            },
            lineStyle: { width: 3 },
            data: this.generateRandomData(120)
          },
          {
            name: this.$t('resourceManagement.storageResources'),
            type: 'line',
            smooth: 0.4,
            showSymbol: false,
            lineStyle: { width: 2 },
            data: this.generateRandomData(80)
          },
          {
            name: this.$t('resourceManagement.networkResources'),
            type: 'line',
            smooth: 0.4,
            showSymbol: false,
            lineStyle: { width: 2 },
            data: this.generateRandomData(65)
          },
          {
            name: this.$t('resourceManagement.memoryResources'),
            type: 'line',
            smooth: 0.4,
            showSymbol: false,
            lineStyle: { width: 2 },
            data: this.generateRandomData(45)
          }
        ]
      }
    },
    
    distributionChartOption() {
      return {
        color: CHART_COLORS,
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          borderColor: '#f0f2f5',
          borderWidth: 1,
          padding: [10, 14],
          textStyle: { color: '#303133', fontSize: 13 },
          extraCssText: 'box-shadow: 0 4px 16px rgba(0,0,0,0.08); border-radius: 8px;',
          formatter: (params) => {
            return `<div style="display: flex; align-items: center; gap: 8px;">
              <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${params.color};"></span>
              <span style="color: #606266;">${params.name}:</span>
              <span style="font-weight: 600;">${params.value}%</span>
            </div>`;
          }
        },
        legend: {
          orient: 'vertical',
          right: '5%',
          top: 'middle',
          icon: 'circle',
          itemWidth: 8,
          itemHeight: 8,
          textStyle: { fontSize: 12, color: '#909399', padding: [0, 0, 0, 4] },
          itemGap: 12
        },
        series: [
          {
            name: this.$t('resourceManagement.resourceUsage'),
            type: 'pie',
            radius: ['55%', '85%'],
            center: ['35%', '50%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 8,
              borderColor: '#fff',
              borderWidth: 3
            },
            label: { show: false },
            emphasis: {
              scale: true,
              scaleSize: 5,
              label: {
                show: true,
                fontSize: '14',
                fontWeight: 'bold',
                formatter: '{b}\n{d}%'
              }
            },
            data: this.reportData.distributionData.map(item => ({
              value: item.percentage,
              name: item.type
            }))
          }
        ]
      }
    }
  },
  methods: {
    hasPermission,
    getReportTypeText(type) {
      const map = {
        'usage_summary': this.$t('resourceManagement.usageSummary'),
        'trend_analysis': this.$t('resourceManagement.trendAnalysis'),
        'cost_analysis': this.$t('resourceManagement.costAnalysis'),
        'forecast_report': this.$t('resourceManagement.forecastReport')
      }
      return map[type] || type
    },
    getChartColor(index) {
      return CHART_COLORS[index % CHART_COLORS.length]
    },
    getAlertType(type) {
      const map = { 'warning': 'warning', 'error': 'danger', 'info': 'info' }
      return map[type] || 'info'
    },
    getImpactClass(impact) {
      const map = { 'high': 'text-danger', 'medium': 'text-warning', 'low': 'text-info' }
      return map[impact] || ''
    },
    getRecIcon(type) {
      const map = { 'optimization': 'el-icon-setting', 'warning': 'el-icon-warning-outline', 'info': 'el-icon-info' }
      return map[type] || 'el-icon-info'
    },
    formatDateTime(date) {
      if (!date) return ''
      const d = new Date(date)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    },
    async handlePreviewReport() {
      this.reportVisible = true
      this.reportLoading = true
      
      try {
        const reportParams = {
          ...this.reportForm,
          timeRange: this.reportForm.timeRange.join(',')
        }
        
        const data = await this.$store.dispatch('resource/generateResourceReport', reportParams)
        if (data) {
          this.reportData = data
        }
        this.reportLoading = false
      } catch (error) {
        console.error('Failed to preview report:', error)
        this.$message.error(this.$t('common.operationFailed'))
        this.reportLoading = false
      }
    },
    
    async handleGenerateReport() {
      this.reportVisible = true
      this.reportLoading = true
      
      try {
        const reportParams = {
          ...this.reportForm,
          timeRange: this.reportForm.timeRange.join(',')
        }
        
        const data = await this.$store.dispatch('resource/generateResourceReport', reportParams)
        if (data) {
          this.reportData = data
        }
        
        this.reportLoading = false
        this.$message.success(this.$t('resourceManagement.reportGenerated'))
      } catch (error) {
        console.error('Failed to generate report:', error)
        this.reportLoading = false
        this.$message.error(this.$t('common.operationFailed'))
      }
    },
    
    async handleExportReport() {
      try {
        this.exportLoading = true
        
        const reportParams = {
          ...this.reportForm,
          timeRange: this.reportForm.timeRange.join(',')
        }
        
        const blob = await this.$store.dispatch('resource/exportResourceReport', reportParams)
        if (blob) {
          this.downloadFile(blob, `资源报表_${new Date().toLocaleDateString()}.xlsx`)
          this.$message.success(this.$t('common.exportSuccess'))
        }
      } catch (error) {
        console.error('Failed to export report:', error)
        this.$message.error(this.$t('common.exportFailed'))
      } finally {
        this.exportLoading = false
      }
    },
    
    downloadFile(blob, fileName) {
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = fileName
      link.click()
      window.URL.revokeObjectURL(link.href)
    },
    
    handleRefresh() {
      if (this.reportVisible) {
        this.handlePreviewReport()
      }
    },
    
    getDateNow() {
      const now = new Date()
      return this.formatDate(now)
    },
    
    getDateBeforeDays(days) {
      const date = new Date()
      date.setDate(date.getDate() - days)
      return this.formatDate(date)
    },
    
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    
    generateTimeLabels() {
      const labels = []
      const startDate = new Date(this.reportForm.timeRange[0])
      const endDate = new Date(this.reportForm.timeRange[1])
      const diffTime = Math.abs(endDate - startDate)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      for (let i = 0; i < diffDays; i++) {
        const date = new Date(startDate)
        date.setDate(date.getDate() + i)
        labels.push(this.formatDate(date).split(' ')[0])
      }
      
      return labels
    },
    
    generateRandomData(base) {
      const data = []
      for (let i = 0; i < 7; i++) {
        data.push(Math.round(base + (Math.random() - 0.5) * base * 0.3))
      }
      return data
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/list-page.scss";

/* 资源报告页面特有样式 */
.list-page-container {
  .report-main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 10px;
    overflow-y: auto;
    padding-right: 4px;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: #f1f5f9; border-radius: 2px; }

    .custom-card {
      border: 1px solid #f1f5f9;
      border-radius: 12px;
      box-shadow: none;

      &:hover {
        border-color: #e2e8f0;
        box-shadow: 0 4px 12px rgba(0,0,0,0.05);
      }

      :deep(.el-card__header) {
        padding: 16px 20px;
        background-color: #fff;
        border-bottom: 1px solid #f1f5f9;

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;

          .header-title {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 15px;
            font-weight: 600;
            color: #1e293b;
            i { color: #409eff; }
          }
        }
      }

      :deep(.el-card__body) {
        padding: 20px;
      }
    }

    .config-form {
      .form-actions {
        margin-top: 20px;
        margin-bottom: 0;
        text-align: right;
        .el-button { border-radius: 8px; }
      }
      :deep(.el-form-item__label) { font-weight: 500; color: #64748b; }
      :deep(.el-input__inner) { border-radius: 8px; }
    }

    .report-content {
      .report-header {
        text-align: center;
        margin-bottom: 30px;
        .report-title { font-size: 22px; font-weight: 700; color: #1e293b; margin-bottom: 12px; }
        .report-meta {
          display: flex; justify-content: center; gap: 24px;
          .meta-item {
            font-size: 13px; color: #64748b;
            .label { margin-right: 4px; }
            .value { color: #1e293b; font-weight: 500; }
          }
        }
      }

      .report-section {
        margin-bottom: 24px;
        .section-title {
          display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 600; color: #1e293b;
          margin-bottom: 16px; padding-left: 12px; border-left: 4px solid #409eff;
        }

        .overview-grid {
          display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;
          .overview-stat-card {
            background: #fff; border: 1px solid #f1f5f9; border-radius: 12px; padding: 16px;
            display: flex; align-items: flex-start; gap: 12px; transition: all 0.2s;
            &:hover { border-color: #409eff; transform: translateY(-2px); }
            .stat-icon {
              width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 20px;
              &.usage { background: rgba(64, 158, 255, 0.1); color: #409eff; }
              &.average { background: rgba(103, 194, 58, 0.1); color: #67c23a; }
              &.peak { background: rgba(230, 162, 60, 0.1); color: #e6a23c; }
              &.cost { background: rgba(245, 108, 108, 0.1); color: #f56c6c; }
            }
            .stat-info {
              flex: 1;
              .stat-label { font-size: 12px; color: #64748b; margin-bottom: 4px; }
              .stat-value-wrapper {
                display: flex; align-items: baseline; gap: 4px; margin-bottom: 4px;
                .stat-value { font-size: 18px; font-weight: 700; color: #1e293b; }
                .stat-unit { font-size: 11px; color: #94a3b8; }
              }
              .stat-change {
                display: flex; align-items: center; gap: 4px; font-size: 11px;
                &.up { color: #10b981; } &.down { color: #ef4444; }
                .change-label { color: #94a3b8; margin-left: 4px; }
              }
            }
          }
        }

        .card-style { background: #fff; border: 1px solid #f1f5f9; border-radius: 12px; padding: 16px; }

        .recommendations-list {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px;
          .recommendation-card {
            display: flex; gap: 12px; padding: 16px; background: #fff; border: 1px solid #f1f5f9; border-radius: 12px; position: relative;
            &::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; border-radius: 4px 0 0 4px; }
            &.optimization { &::before { background-color: #409eff; } .rec-icon { color: #409eff; background: rgba(64, 158, 255, 0.1); } }
            &.warning { &::before { background-color: #e6a23c; } .rec-icon { color: #e6a23c; background: rgba(230, 162, 60, 0.1); } }
            &.info { &::before { background-color: #909399; } .rec-icon { color: #909399; background: rgba(144, 147, 153, 0.1); } }
            .rec-icon { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
            .rec-body {
              flex: 1;
              .rec-title { font-size: 14px; font-weight: 600; color: #1e293b; margin-bottom: 4px; }
              .rec-desc { font-size: 12px; color: #64748b; line-height: 1.5; }
            }
          }
        }
      }
    }
  }
}

.text-primary { color: #409eff; }
.text-secondary { color: #64748b; }
.text-success { color: #22c55e; }
.text-danger { color: #ef4444; }
.text-warning { color: #f97316; }
.font-medium { font-weight: 500; }
.font-bold { font-weight: 700; }
.ml-1 { margin-left: 4px; }

@media screen and (max-width: 1024px) {
  .list-page-container {
    .report-main-content {
      .report-content {
        .report-section {
          .overview-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      }
    }
  }
  .filter-date-picker {
    width: 350px !important;
  }
}

@media screen and (max-width: 768px) {
  .list-page-container {
    .control-panel {
      .control-container {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
        .action-wrapper { width: 100%; justify-content: space-between; }
      }
    }
    .report-main-content {
      .report-content {
        .report-header {
          .report-meta { gap: 12px; flex-direction: column; align-items: center; }
        }
        .report-section {
          .overview-grid { grid-template-columns: 1fr; }
          .recommendations-list { grid-template-columns: 1fr; }
        }
      }
    }
  }
}

@media screen and (max-width: 375px) {
  .list-page-container {
    .control-panel {
      .control-container {
        .action-wrapper {
          flex-wrap: wrap;
          .el-button { flex: 1 1 45%; }
        }
      }
    }
  }
}
</style>
